import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';
import timezone from 'dayjs/plugin/timezone';
import { currentTimezoneCode } from '../../../utils/timezoneCodes';

dayjs.extend(utc);
dayjs.extend(timezone);

/*
 * Holiday days for the dashboard.
 *
 * A holiday closes whole days in the account zone, from `startDay` to `endDay`, both
 * inclusive, as "YYYY-MM-DD" strings. The page shows and edits those strings as plain
 * calendar dates, with no zone conversion. Records saved before the day strings existed
 * carry only `startDate` and `endDate`. The page reads those with the same rule as the
 * booking engine (api src/holidayDays.ts), so the list shows the days the engine closes.
 */

const DAY_PATTERN = /^\d{4}-\d{2}-\d{2}$/;
const FALLBACK_ZONE = 'Europe/London';

// True for a real calendar date in the form YYYY-MM-DD. The round trip refuses 2099-02-31.
export function isCalendarDay(value) {
  return (
    typeof value === 'string' &&
    DAY_PATTERN.test(value) &&
    dayjs.utc(value).format('YYYY-MM-DD') === value
  );
}

// The account zone that dayjs can read. Legacy codes map to their current names. An
// unknown or missing zone falls back to Europe/London, as the Reports page does.
export function accountZone(user) {
  const zone = currentTimezoneCode(user?.config?.timezone);
  if (typeof zone !== 'string' || !zone) return FALLBACK_ZONE;
  try {
    Intl.DateTimeFormat('en-US', { timeZone: zone });
    return zone;
  } catch (error) {
    return FALLBACK_ZONE;
  }
}

/*
 * The local day a legacy instant stands for in `zone`. The API calls this the nearest
 * local midnight: a local wall time of 12:00 or later stands for the next day. An exact
 * local midnight, or the first instant after a midnight that falls in a DST gap, always
 * has a wall time before 12:00, so it keeps its own day.
 */
function legacyDay(instant, zone) {
  const local = dayjs(instant).tz(zone);
  const day = local.format('YYYY-MM-DD');
  if (local.format('HH:mm') < '12:00') return day;
  return dayjs.utc(day).add(1, 'day').format('YYYY-MM-DD');
}

function isInstant(value) {
  if (value == null) return false;
  if (!(value instanceof Date) && typeof value !== 'string' && typeof value !== 'number') return false;
  return dayjs(value).isValid();
}

/*
 * The first and last day a holiday record closes, in `zone`. The stored day strings win
 * when both are real dates. Otherwise both days come from the legacy instants. Null when
 * neither shape is readable.
 */
export function holidayDaysOf(record, zone) {
  if (isCalendarDay(record?.startDay) && isCalendarDay(record?.endDay)) {
    return { startDay: record.startDay, endDay: record.endDay };
  }
  if (!isInstant(record?.startDate) || !isInstant(record?.endDate)) return null;
  return {
    startDay: legacyDay(record.startDate, zone),
    endDay: legacyDay(record.endDate, zone),
  };
}

// The number of days from `startDay` to `endDay`, both inclusive. One for a one-day holiday.
export function dayCount(startDay, endDay) {
  return dayjs.utc(endDay).diff(dayjs.utc(startDay), 'day') + 1;
}

// A day string for people, such as "25 December 2026".
export function formatDay(day) {
  return dayjs.utc(day).format('D MMMM YYYY');
}

/*
 * The local midnight of a day in the account zone, as an ISO instant. An API before the
 * day strings reads only `startDate` and `endDate`, and stores this value. The API with
 * the day strings ignores them when `startDay` and `endDay` are present, and writes this
 * same value itself. The browser's own midnight is not used: a browser 12 or more hours
 * away from the account zone would store an instant that the day rule reads as another day.
 * When midnight falls in a DST gap, dayjs gives the first instant after the gap, as the API does.
 */
export function accountMidnight(day, zone) {
  return dayjs.tz(day, zone).toISOString();
}

// The value for a date picker, which works in the browser's local calendar. Null for none.
export function pickerValue(day) {
  return isCalendarDay(day) ? dayjs(day) : null;
}

// The day string a date picker value names, or null when the value is empty or unfinished.
export function dayOfPickerValue(value) {
  if (!value || !value.isValid()) return null;
  return value.format('YYYY-MM-DD');
}
